import React, { FC } from "react";
import { Card, Icon, Input, Label } from "@telia/teddy";
import { CopyButton } from "../../../components/atoms/CopyButton";
import { CustomerAddress, OrderDetails } from "../../../types/models";
import OrderDetailSection from "./OrderDetailSection";

interface PayerSectionProps {
    order: OrderDetails;
}

interface CustomerFieldProps {
    id: string;
    label: string;
    value?: string | null;
    fullWidth?: boolean;
}

const formatAddress = (address?: CustomerAddress): string => {
    if (!address) return "";
    const streetNumber = address.streetNumber ? `${address.streetNumber}${address.streetNumberSuffix ?? ""}` : null;
    const streetLine = [address.streetName, streetNumber].filter(Boolean).join(" ");
    const postalLine = [address.zipCode, address.city].filter(Boolean).join(" ");
    return [streetLine, postalLine].filter(Boolean).join(", ");
};

const CustomerField: FC<CustomerFieldProps> = ({ id, label, value, fullWidth }) => (
    <div className={`order-detail__customer-field${fullWidth ? " order-detail__customer-field--full" : ""}`}>
        <Label htmlFor={id}>{label}</Label>
        <div className="order-detail__customer-field-row">
            <div className="order-detail__customer-field-input">
                <Input id={id} type="text" value={value ?? ""} readOnly data-di-mask />
            </div>
            <CopyButton value={value} ariaLabel={`Kopier ${label.toLowerCase()}`} toastMessage={`${label} kopiert`} />
        </div>
    </div>
);

const PayerSection: FC<PayerSectionProps> = ({ order }) => {
    const payer = order.customer?.payer;
    if (!payer) return null;

    const fullName =
        payer.type === "organization"
            ? (payer.organizationName ?? "")
            : [payer.firstName, payer.lastName].filter(Boolean).join(" ");
    const addressLine = formatAddress(payer.address);

    return (
        <OrderDetailSection title="Betaler">
            <Card variant="white" bordered className="order-detail__summary-card order-detail__summary-card--customer">
                <div className="order-detail__customer-card-heading">
                    <Icon name="end-user" size="md" aria-hidden="true" focusable="false" />
                    <Card.Heading variant="title-100">{fullName}</Card.Heading>
                    <CopyButton value={fullName} ariaLabel="Kopier navn" toastMessage="Navn kopiert" />
                </div>
                <Card.Line />
                <Card.Content className="order-detail__customer-card-content">
                    <div className="order-detail__customer-grid">
                        {payer.type === "organization" ? (
                            <>
                                <CustomerField
                                    id="payer-organization-name"
                                    label="Bedriftsnavn"
                                    value={payer.organizationName}
                                />
                                <CustomerField
                                    id="payer-organization-number"
                                    label="Org. nummer"
                                    value={payer.organizationNumber}
                                />
                            </>
                        ) : (
                            <>
                                <CustomerField id="payer-first-name" label="Fornavn" value={payer.firstName} />
                                <CustomerField id="payer-last-name" label="Etternavn" value={payer.lastName} />
                            </>
                        )}
                        <CustomerField id="payer-address" label="Adresse" value={addressLine} fullWidth />
                    </div>
                </Card.Content>
            </Card>
        </OrderDetailSection>
    );
};

export default PayerSection;
